import type { Ball } from './ball.js'
import type { Hoop } from './hoop.js'
import { applyFriction } from './surface.js'
import { BOUNDARY_RESTITUTION, STOP_THRESHOLD } from './constants.js'

export interface CourtBounds {
  minX: number
  maxX: number
  minY: number
  maxY: number
}

export type StepResult = {
  hoopHit: number | null   // index of hoop upright hit ('left'/'right' mapped to index)
  scored: boolean          // ball passed through a hoop
  stopped: boolean         // ball came to rest
}

/**
 * Advance the simulation by one timestep.
 *
 * Order of operations per step:
 *   1. Apply friction (decelerates ball)
 *   2. Advance position
 *   3. Resolve hoop upright collisions (circle-circle)
 *   4. Check hoop scoring (line-crossing)
 *   5. Resolve boundary collisions
 *
 * @param ball    The moving ball (mutated)
 * @param prevPos Ball position at the start of this frame (for scoring detection)
 * @param hoops   All hoops in play (usually just the current one for Bamford)
 * @param bounds  Court boundary
 * @param dt      Timestep in seconds (keep ≤ 1/60 for stability)
 */
export function stepPhysics(
  ball: Ball,
  prevPos: { x: number; y: number },
  hoops: Hoop[],
  bounds: CourtBounds,
  dt: number
): StepResult {
  const result: StepResult = { hoopHit: null, scored: false, stopped: false }

  if (ball.state === 'stopped') {
    result.stopped = true
    return result
  }

  // 1. Friction
  applyFriction(ball, dt)

  // applyFriction may have stopped the ball — check speed rather than state
  // to avoid TypeScript narrowing the state type from the early-return guard above
  if (ball.speed === 0) {
    result.stopped = true
    return result
  }

  // 2. Advance position
  ball.pos.x += ball.vel.x * dt
  ball.pos.y += ball.vel.y * dt

  // 3. Hoop upright collisions + scoring
  for (let i = 0; i < hoops.length; i++) {
    const hoop = hoops[i]
    const hit = hoop.resolveCollisions(ball)
    if (hit !== 'none') {
      result.hoopHit = i
    }
    if (hoop.checkScoring(prevPos, ball.pos)) {
      result.scored = true
    }
  }

  // 4. Boundary collision
  resolveBoundary(ball, bounds)

  // 5. Final stop check
  if (ball.speed < STOP_THRESHOLD) {
    ball.stop()
    result.stopped = true
  }

  return result
}

function resolveBoundary(ball: Ball, bounds: CourtBounds): void {
  const r = ball.radius
  if (ball.pos.x - r < bounds.minX) {
    ball.pos.x = bounds.minX + r
    ball.vel.x = Math.abs(ball.vel.x) * BOUNDARY_RESTITUTION
  } else if (ball.pos.x + r > bounds.maxX) {
    ball.pos.x = bounds.maxX - r
    ball.vel.x = -Math.abs(ball.vel.x) * BOUNDARY_RESTITUTION
  }
  if (ball.pos.y - r < bounds.minY) {
    ball.pos.y = bounds.minY + r
    ball.vel.y = Math.abs(ball.vel.y) * BOUNDARY_RESTITUTION
  } else if (ball.pos.y + r > bounds.maxY) {
    ball.pos.y = bounds.maxY - r
    ball.vel.y = -Math.abs(ball.vel.y) * BOUNDARY_RESTITUTION
  }
}
